/**
 * Message Thread Component
 * Displays messages and allows sending new messages - WhatsApp style
 */

import React, { useState, useEffect, useRef } from 'react';
import { Send, ArrowLeft, Plus, Mic, X, Image as ImageIcon, File, Video, Search, MoreVertical, Users, Bell, BellOff, Trash2 } from 'lucide-react';
import ParticipantsModal from './ParticipantsModal';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { messagingService } from '@/firebase/messagingService';
import { Conversation, Message, MessageAttachment, ConversationParticipant } from '@/types/messaging';
import MessageBubble from './MessageBubble';
import ForwardMessageModal from './ForwardMessageModal';
import { uploadMessageAttachment, uploadAudioRecording, formatFileSize } from '@/utils/messagingAttachments';
import { userService } from '@/firebase/database';
import FileSizeErrorModal from '../FileSizeErrorModal';
import AddTaskPanel from '../AddTaskPanel';
import { messagingIntegrationService } from '@/services/messagingIntegrationService';
import ViewTaskModal from '../ViewTaskModal';
import { TaskWithDetails } from '@/services/taskService';
import { AgreementDocument } from '@/firebase/agreementService';
import { Vendor } from '@/firebase/vendorService';
import { ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { checklistService } from '@/firebase/checklistService';
import { Checklist } from '@/firebase/checklistService';
import { playNotificationSound, showMessageNotification, isWindowFocused, updateBrowserTabBadge, clearBrowserTabBadge, initBrowserTabBadge } from '@/utils/messageNotifications';
import { useNotification } from '@/contexts/NotificationContext';
import { trackMessaging } from '@/utils/analytics';
import MentionAutocomplete from './MentionAutocomplete';
import { parseMentions, extractMentionQuery, insertMention, UserOption } from '@/utils/mentionParser';
import { db } from '@/firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';

interface MessageThreadProps {
  conversation: Conversation;
  onBack?: () => void;
}

const MessageThread: React.FC<MessageThreadProps> = ({ conversation, onBack }) => {
  const { user } = useAuth();
  const { currentOrganization, organizations } = useOrganization();
  const conversationOrgName =
    conversation.type !== 'direct' &&
    conversation.organizationId &&
    organizations?.length
      ? organizations.find((o) => o.id === conversation.organizationId)?.name
      : null;
  const { showNotification } = useNotification();
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [fileSizeError, setFileSizeError] = useState<{ fileName: string; fileSize: number } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const videoChunksRef = useRef<Blob[]>([]);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const [otherParticipantInfo, setOtherParticipantInfo] = useState<{ name: string; picture?: string } | null>(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardMessage, setForwardMessage] = useState<Message | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [showAddTaskPanel, setShowAddTaskPanel] = useState(false);
  const [taskMessageContext, setTaskMessageContext] = useState<{
    message: Message;
    conversation: Conversation;
  } | null>(null);
  const [viewingTask, setViewingTask] = useState<TaskWithDetails | null>(null);
  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [viewingContract, setViewingContract] = useState<AgreementDocument | null>(null);
  const [viewingChecklistCompletion, setViewingChecklistCompletion] = useState<ChecklistCompletion | null>(null);
  const [viewingChecklist, setViewingChecklist] = useState<Checklist | null>(null);
  const [isLoadingChecklistDetails, setIsLoadingChecklistDetails] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [participantData, setParticipantData] = useState<ConversationParticipant | null>(null);
  const menuDropdownRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousMessageIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef<boolean>(true);
  const hasClearedBadgeForConversationRef = useRef<string | null>(null);
  const loadedMessageIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedMoreRef = useRef<boolean>(false);
  
  // Mention autocomplete state
  const [mentionUsers, setMentionUsers] = useState<UserOption[]>([]);
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const mentionAutocompleteRef = useRef<HTMLDivElement>(null);

  // Load other participant info for direct messages
  useEffect(() => {
    const loadParticipantInfo = async () => {
      if (conversation.type === 'direct' && conversation.participants) {
        const otherId = conversation.participants.find(id => id !== user?.id);
        if (otherId) {
          try {
            const userResult = await userService.getUser(otherId);
            if (userResult.success && userResult.data) {
              setOtherParticipantInfo({
                name: userResult.data.name || userResult.data.email || 'Unknown',
                picture: userResult.data.picture,
              });
            }
          } catch (error) {
            console.error('Error loading participant info:', error);
          }
        }
      } else if (conversation.type === 'group' || conversation.type === 'task' || conversation.type === 'contract' || conversation.type === 'checklist' || conversation.type === 'form') {
        const typeLabels: Record<string, string> = {
          group: 'Group',
          task: 'Task',
          contract: 'Contract',
          checklist: 'Checklist',
          form: 'Form'
        };
        setOtherParticipantInfo({
          name: conversation.name || typeLabels[conversation.type] || 'Conversation',
        });
      }
    };
    loadParticipantInfo();
  }, [conversation, user?.id]);

  // Initialize browser tab badge when conversation changes
  useEffect(() => {
    initBrowserTabBadge();
    // Reset the cleared badge flag when conversation changes
    hasClearedBadgeForConversationRef.current = null;
    // Don't clear badge on unmount - let it persist across navigation
  }, [conversation.id]);

  // Clear badge when user opens/views a conversation (only on initial load, not on new messages)
  useEffect(() => {
    // Clear badge only once when conversation is first opened/viewed (after initial messages load)
    if (conversation.id && messages.length > 0 && hasClearedBadgeForConversationRef.current !== conversation.id && !isInitialLoadRef.current) {
      clearBrowserTabBadge();
      hasClearedBadgeForConversationRef.current = conversation.id;
    }
  }, [conversation.id, messages.length]);

  // Load participant data to check mute status
  useEffect(() => {
    const loadParticipantData = async () => {
      if (!conversation.id || !user?.id) return;
      
      try {
        const result = await messagingService.getConversationParticipant(conversation.id, user.id);
        if (result.success && result.data) {
          setParticipantData(result.data);
        }
      } catch (error) {
        console.error('Error loading participant data:', error);
      }
    };
    loadParticipantData();
  }, [conversation.id, user?.id]);

  // Load users for mention autocomplete (conversation participants)
  useEffect(() => {
    const loadMentionUsers = async () => {
      if (!conversation.participants || conversation.participants.length === 0) {
        setMentionUsers([]);
        return;
      }

      try {
        const usersList: UserOption[] = [];
        const participantIds = conversation.participants.filter(id => id !== user?.id); // Exclude current user

        if (participantIds.length === 0) {
          setMentionUsers([]);
          return;
        }

        // Fetch user details for each participant
        const userPromises = participantIds.map(async (participantId): Promise<UserOption | null> => {
          try {
            const userResult = await userService.getUser(participantId);
            if (userResult.success && userResult.data) {
              return {
                id: participantId,
                name: userResult.data.name || userResult.data.email || 'Unknown User',
                email: userResult.data.email || '',
                picture: userResult.data.picture,
              };
            }
          } catch (error) {
            console.error(`Error loading user ${participantId}:`, error);
          }
          return null;
        });

        const loadedUsersResults = await Promise.all(userPromises);
        const loadedUsers: UserOption[] = loadedUsersResults.filter((u): u is UserOption => u !== null);
        const sortedUsers = loadedUsers.sort((a, b) => a.name.localeCompare(b.name));
        setMentionUsers(sortedUsers);
      } catch (error) {
        console.error('Error loading mention users:', error);
        setMentionUsers([]);
      }
    };

    loadMentionUsers();
  }, [conversation.participants, user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target as Node)) {
        setShowMenuDropdown(false);
      }
    };

    if (showMenuDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenuDropdown]);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sync participants for task conversations when conversation is loaded
  useEffect(() => {
    const syncTaskParticipants = async () => {
      // Only sync if this is a task conversation
      // Double-check: must have taskId AND be type 'task' (not regular 'group')
      const taskId = conversation.linkedItems?.taskId;
      if (!taskId || !conversation.id || !user?.id || !currentOrganization?.id) {
        return;
      }
      
      // Additional safeguard: Only sync actual task conversations, not regular groups
      if (conversation.type !== 'task') {
        console.log('[MessageThread] Skipping sync - not a task conversation type');
        return;
      }

      try {
        // Fetch task to get locationId and ownerRole
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const taskRef = doc(db, 'tasks', taskId);
        const taskSnap = await getDoc(taskRef);

        if (taskSnap.exists()) {
          const taskData = taskSnap.data();
          const taskLocationId = taskData.locationId;
          const taskOwnerRole = taskData.ownerRole;

          if (taskLocationId) {
            // Sync participants (this will add/remove users based on current access)
            await messagingIntegrationService.syncTaskConversationParticipants(
              conversation.id,
              taskId,
              taskLocationId,
              currentOrganization.id,
              user.id,
              taskOwnerRole
            );
          }
        }
      } catch (error) {
        console.error('Error syncing task conversation participants:', error);
        // Don't block the UI if sync fails
      }
    };

    syncTaskParticipants();
  }, [conversation.id, conversation.linkedItems?.taskId, user?.id, currentOrganization?.id]);

  // Subscribe to messages
  useEffect(() => {
    if (!conversation.id) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Load initial messages
    messagingService.getMessages(conversation.id, 50).then((result) => {
      if (result.success && result.data) {
        setMessages(result.data);
        // Track initial message IDs
        result.data.forEach((msg) => {
          if (msg.id) {
            loadedMessageIdsRef.current.add(msg.id);
          }
        });
        // Check if there are more messages to load (if we got 50, there might be more)
        setHasMoreMessages(result.data.length === 50);
        hasLoadedMoreRef.current = false; // Reset on initial load
      }
      setLoading(false);
    });

    // Subscribe to new messages
    const unsubscribe = messagingService.subscribeToMessages(
      conversation.id!,
      (newMessages) => {
        
        // Detect new messages from others
        if (!isInitialLoadRef.current && user?.id) {
          const currentMessageIds = new Set<string>(
            newMessages.map(m => m.id).filter((id): id is string => Boolean(id))
          );
          const newMessageIds = Array.from(currentMessageIds).filter(
            id => !previousMessageIdsRef.current.has(id)
          );

          // Check for new messages from other users
          if (newMessageIds.length > 0) {
            const newMessagesFromOthers = newMessages.filter(
              m => m.id && newMessageIds.includes(m.id) && m.sentBy !== user.id
            );

            if (newMessagesFromOthers.length > 0) {
              const latestMessage = newMessagesFromOthers[newMessagesFromOthers.length - 1];
              
              // Always play sound notification
              playNotificationSound();

              const senderName = latestMessage.sentByName || 'Someone';
              const messageText = latestMessage.text || 
                (latestMessage.attachments && latestMessage.attachments.length > 0 
                  ? '📎 Sent an attachment' 
                  : 'Sent a message');
              const conversationName = conversation.type === 'group' 
                ? conversation.name 
                : undefined;

              // Show browser notification if window is not focused
              // Also show if tab is visible but we want to notify user
              const isFocused = isWindowFocused();
              if (!isFocused || document.hidden) {
                showMessageNotification(senderName, messageText, conversationName);
              }

              // Always update browser tab title to show badge/alert for new messages
              // Badge will be cleared when user views the conversation
              updateBrowserTabBadge(newMessagesFromOthers.length);
            }
          }

          // Update previous message IDs
          previousMessageIdsRef.current = currentMessageIds;
        } else {
          // Initial load - just store message IDs
          const messageIds = new Set<string>(
            newMessages.map(m => m.id).filter((id): id is string => Boolean(id))
          );
          previousMessageIdsRef.current = messageIds;
          isInitialLoadRef.current = false;
        }

        // If we've manually loaded more messages, merge instead of replace
        if (hasLoadedMoreRef.current) {
          setMessages((prevMessages) => {
            // Create a map of existing messages by ID
            const existingMessagesMap = new Map<string, Message>();
            prevMessages.forEach((msg) => {
              if (msg.id) {
                existingMessagesMap.set(msg.id, msg);
              }
            });

            // Add new messages from subscription (these are the most recent)
            newMessages.forEach((msg) => {
              if (msg.id) {
                existingMessagesMap.set(msg.id, msg);
              }
            });

            // Convert back to array and sort by createdAt (oldest first)
            const mergedMessages = Array.from(existingMessagesMap.values());
            mergedMessages.sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || 
                           (a.createdAt instanceof Date ? a.createdAt.getTime() : 0) ||
                           (typeof a.createdAt === 'number' ? a.createdAt : 0);
              const bTime = b.createdAt?.toMillis?.() || 
                           (b.createdAt instanceof Date ? b.createdAt.getTime() : 0) ||
                           (typeof b.createdAt === 'number' ? b.createdAt : 0);
              return aTime - bTime;
            });

            return mergedMessages;
          });
        } else {
          // Normal behavior - replace messages (only most recent 50)
          setMessages(newMessages);
          // Update hasMoreMessages if we're not at the initial limit (only check on initial load)
          if (isInitialLoadRef.current && newMessages.length < 50) {
            setHasMoreMessages(false);
          }
        }
        // Mark as read when viewing
        if (user?.id) {
          messagingService.markAsRead(conversation.id!, user.id);
        }
      }
    );

    // Mark conversation as read when opened
    if (user?.id) {
      messagingService.markAsRead(conversation.id!, user.id);
    }

    return () => {
      unsubscribe();
      // Reset refs when conversation changes
      previousMessageIdsRef.current = new Set();
      isInitialLoadRef.current = true;
      loadedMessageIdsRef.current = new Set();
      hasLoadedMoreRef.current = false;
      // Reset pagination state when conversation changes
      setHasMoreMessages(true);
    };
  }, [conversation.id, user?.id]);

  // Cleanup audio recording on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [isRecording, audioUrl]);

  // Cleanup video recording on unmount
  useEffect(() => {
    return () => {
      if (videoRecorderRef.current && isRecordingVideo) {
        videoRecorderRef.current.stop();
      }
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [isRecordingVideo, videoUrl, videoStream]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((!messageText.trim() && attachments.length === 0) || !conversation.id || !user?.id || sending) {
      return;
    }

    const originalText = messageText; // Keep original for parsing
    const textToSend = messageText.trim() || '';
    const attachmentsToSend = [...attachments];
    
    // Parse mentions from original text (before trimming) to get correct indices
    // Then adjust indices for trimmed text
    const leadingWhitespaceCount = originalText.length - originalText.trimStart().length;
    const parsedMentions = parseMentions(originalText, mentionUsers);
    
    // Adjust indices to account for trimmed leading whitespace
    const mentions = parsedMentions.map(mention => ({
      ...mention,
      startIndex: Math.max(0, mention.startIndex - leadingWhitespaceCount),
      endIndex: Math.max(0, mention.endIndex - leadingWhitespaceCount)
    })).filter(mention => 
      mention.startIndex >= 0 && 
      mention.endIndex > mention.startIndex && 
      mention.endIndex <= textToSend.length
    );
    
    setMessageText('');
    setAttachments([]);
    setShowMentionAutocomplete(false);
    setSending(true);

    try {
      const result = await messagingService.sendMessage(
        conversation.id,
        textToSend || (attachmentsToSend.length > 0 ? '📎' : ''),
        user.id,
        attachmentsToSend.length > 0 ? attachmentsToSend : undefined,
        undefined, // transcription
        mentions.length > 0 ? mentions : undefined // mentions
      );

      if (!result.success) {
        alert(`Failed to send message: ${result.error}`);
        setMessageText(textToSend);
        setAttachments(attachmentsToSend);
      } else {
        // Track message sent
        trackMessaging.messageSent(conversation.id, attachmentsToSend.length > 0);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert(`Failed to send message: ${error.message}`);
      setMessageText(textToSend);
      setAttachments(attachmentsToSend);
    } finally {
      setSending(false);
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !conversation.id || !user?.id) return;

    // Validate file sizes before processing (videos can be larger)
    const { validateFileSizeByType, formatFileSize } = await import('@/utils/fileSizeValidation');
    const fileArray = Array.from(files);
    const invalidFiles: { name: string; size: number }[] = [];
    
    fileArray.forEach(file => {
      try {
        validateFileSizeByType(file);
      } catch (error) {
        invalidFiles.push({ name: file.name, size: file.size });
      }
    });
    
    // Show error modal for invalid files (show first invalid file) and return early
    if (invalidFiles.length > 0) {
      const firstInvalid = invalidFiles[0];
      setFileSizeError({ fileName: firstInvalid.name, fileSize: firstInvalid.size });
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploading(true);

    try {
      // First, create preview attachments with local URLs for images and videos
      const previewAttachments: MessageAttachment[] = [];
      
      for (const file of fileArray) {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        const previewUrl = (isImage || isVideo) ? URL.createObjectURL(file) : undefined;
        
        let type: 'image' | 'file' | 'video' = 'file';
        if (isImage) type = 'image';
        else if (isVideo) type = 'video';
        
        previewAttachments.push({
          type,
          url: previewUrl || '',
          name: file.name,
          size: file.size,
        } as MessageAttachment);
      }

      // Add preview attachments immediately for instant display
      setAttachments((prev) => [...prev, ...previewAttachments]);

      // Upload files and update URLs
      const uploadPromises = fileArray.map(async (file, index) => {
        const result = await uploadMessageAttachment(file, conversation.id!, user.id);
        return {
          index,
          result: {
            type: result.type as 'image' | 'file' | 'video',
            url: result.url,
            name: result.name,
            size: result.size,
          } as MessageAttachment,
          previewUrl: previewAttachments[index].url,
        };
      });

      const uploadedResults = await Promise.all(uploadPromises);
      
      // Update attachments with uploaded URLs by matching name and size
      setAttachments((prev) => {
        const updated = [...prev];
        uploadedResults.forEach(({ index, result, previewUrl }) => {
          const previewAttachment = previewAttachments[index];
          const attachmentIndex = updated.findIndex(
            (att, idx) =>
              idx >= updated.length - previewAttachments.length &&
              att.name === previewAttachment.name &&
              att.size === previewAttachment.size &&
              att.url === previewUrl
          );
          
          if (attachmentIndex >= 0) {
            if (previewUrl && previewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(previewUrl);
            }
            updated[attachmentIndex] = result;
          }
        });
        return updated;
      });
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const errorMessage = error.message || 'Unknown error occurred';
      alert(`Failed to upload file: ${errorMessage}`);
      setAttachments((prev) => prev.slice(0, prev.length - fileArray.length));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => {
      const attachment = prev[index];
      if (attachment && attachment.url.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error: any) {
      console.error('Error starting audio recording:', error);
      alert(`Failed to start recording: ${error.message || 'Microphone access denied'}`);
    }
  };

  const stopAudioRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const cancelAudioRecording = () => {
    stopAudioRecording();
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  };

  const sendAudioRecording = async () => {
    if (!audioBlob || !conversation.id || !user?.id) return;

    setUploading(true);

    try {
      const result = await uploadAudioRecording(audioBlob, conversation.id, user.id);
      
      const audioAttachment: MessageAttachment = {
        type: 'audio',
        url: result.url,
        name: result.name,
        size: result.size,
      };

      setAttachments((prev) => [...prev, audioAttachment]);
      cancelAudioRecording();
    } catch (error: any) {
      console.error('Error uploading audio:', error);
      alert(`Failed to upload audio: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const formatRecordingTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setVideoStream(stream);
      
      // Show preview
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'video/mp4'
      });
      videoRecorderRef.current = mediaRecorder;
      videoChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const videoBlob = new Blob(videoChunksRef.current, { 
          type: MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'video/mp4' 
        });
        setVideoBlob(videoBlob);
        const url = URL.createObjectURL(videoBlob);
        setVideoUrl(url);
        
        stream.getTracks().forEach((track) => track.stop());
        setVideoStream(null);
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
      };

      mediaRecorder.start();
      setIsRecordingVideo(true);
      setRecordingTime(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error: any) {
      console.error('Error starting video recording:', error);
      alert(`Failed to start video recording: ${error.message || 'Camera access denied'}`);
    }
  };

  const stopVideoRecording = () => {
    if (videoRecorderRef.current && isRecordingVideo) {
      videoRecorderRef.current.stop();
      setIsRecordingVideo(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const cancelVideoRecording = () => {
    stopVideoRecording();
    setVideoBlob(null);
    setVideoUrl(null);
    setRecordingTime(0);
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
  };

  const sendVideoRecording = async () => {
    if (!videoBlob || !conversation.id || !user?.id) return;

    setUploading(true);

    try {
      // Create a File object from the blob for upload
      const fileName = `video_${Date.now()}.${videoBlob.type.includes('webm') ? 'webm' : 'mp4'}`;
      // @ts-ignore - File constructor accepts 3 args in browser environment
      const videoFile = new File([videoBlob], fileName, {
        type: videoBlob.type || 'video/webm'
      });
      
      const result = await uploadMessageAttachment(videoFile, conversation.id, user.id);
      
      const videoAttachment: MessageAttachment = {
        type: 'video',
        url: result.url,
        name: result.name,
        size: result.size,
      };

      setAttachments((prev) => [...prev, videoAttachment]);
      cancelVideoRecording();
    } catch (error: any) {
      console.error('Error uploading video:', error);
      alert(`Failed to upload video: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Get filtered users for navigation (matches what autocomplete shows)
  const getFilteredMentionUsers = () => {
    if (!mentionQuery.trim()) return mentionUsers;
    const lowerQuery = mentionQuery.toLowerCase().trim();
    return mentionUsers.filter((user) => {
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(lowerQuery) || email.includes(lowerQuery);
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle mention autocomplete navigation
    if (showMentionAutocomplete) {
      const filteredUsers = getFilteredMentionUsers();
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIndex((prev) => 
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const selectedUser = filteredUsers[mentionSelectedIndex];
        if (selectedUser) {
          handleSelectMention(selectedUser);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionAutocomplete(false);
        return;
      }
    }

    // Note: @ key detection is handled in handleMessageTextChange
    // because we need the updated text value which isn't available in keyDown

    // Handle Enter to send (when not in mention autocomplete)
    if (e.key === 'Enter' && !e.shiftKey && !showMentionAutocomplete) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleSelectMention = (selectedUser: UserOption) => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    // The @ symbol is already in the text at mentionStartIndex
    // We need to replace everything from after @ to cursor with the username
    // Keep: text before @, the @ itself, then add username
    const textBeforeAt = messageText.substring(0, mentionStartIndex);
    const textAfterCursor = messageText.substring(mentionCursorPosition);
    
    // Build new text: everything before @ + @ + username + space + everything after cursor
    const newText = `${textBeforeAt}@${selectedUser.name} ${textAfterCursor}`;
    setMessageText(newText);
    setShowMentionAutocomplete(false);
    setMentionQuery('');
    
    // Set cursor position after the mention (after @username and space)
    setTimeout(() => {
      const newCursorPos = mentionStartIndex + 1 + selectedUser.name.length + 1; // +1 for @, +1 for space
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  // Calculate position of @ symbol in textarea using Range API
  const calculateMentionPosition = (textarea: HTMLTextAreaElement, atIndex: number) => {
    // Create a mirror div to measure text position
    const mirror = document.createElement('div');
    const computed = window.getComputedStyle(textarea);
    
    // Copy relevant styles
    const stylesToCopy = [
      'font', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle',
      'letterSpacing', 'textTransform', 'wordSpacing', 'textIndent',
      'whiteSpace', 'wordWrap', 'paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom',
      'borderLeftWidth', 'borderRightWidth', 'borderTopWidth', 'borderBottomWidth',
      'boxSizing', 'width'
    ];
    
    stylesToCopy.forEach(prop => {
      mirror.style[prop as any] = computed[prop as any];
    });
    
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.top = '0';
    mirror.style.left = '0';
    mirror.style.height = 'auto';
    mirror.style.overflow = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordWrap = 'break-word';
    
    // Get text before @
    const textBeforeAt = messageText.substring(0, atIndex);
    
    // Split by newlines to handle multiline
    const lines = messageText.substring(0, atIndex).split('\n');
    const currentLine = lines[lines.length - 1];
    
    // Create a span to measure the width of text before @ on current line
    const measureSpan = document.createElement('span');
    measureSpan.textContent = currentLine;
    mirror.appendChild(measureSpan);
    
    document.body.appendChild(mirror);
    
    // Get measurements
    const textWidth = measureSpan.offsetWidth;
    const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2;
    
    // Calculate which line we're on (accounting for scroll)
    const textareaRect = textarea.getBoundingClientRect();
    const paddingTop = parseFloat(computed.paddingTop) || 0;
    const paddingLeft = parseFloat(computed.paddingLeft) || 0;
    
    // Calculate vertical position
    const lineNumber = lines.length - 1;
    const top = textareaRect.top + paddingTop + (lineNumber * lineHeight) - textarea.scrollTop;
    
    // Calculate horizontal position
    const left = textareaRect.left + paddingLeft + textWidth;
    
    // Clean up
    document.body.removeChild(mirror);
    
    return { top, left };
  };

  // Handle text input change to detect mention queries
  const handleMessageTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    setMessageText(newText);
    setMentionCursorPosition(cursorPos);

    // Check if we're in a mention context
    const mentionInfo = extractMentionQuery(newText, cursorPos);
    if (mentionInfo && messageInputRef.current) {
      setMentionStartIndex(mentionInfo.startIndex);
      setMentionQuery(mentionInfo.query);
      setMentionSelectedIndex(0);
      
      // Calculate position of @ symbol
      const position = calculateMentionPosition(messageInputRef.current, mentionInfo.startIndex);
      setMentionPosition(position);
      
      // Show autocomplete if we have users (or show empty state)
      setShowMentionAutocomplete(true);
    } else {
      setShowMentionAutocomplete(false);
    }
  };

  // Load conversations for forwarding
  const loadConversationsForForward = async () => {
    if (!user?.id || !currentOrganization?.id) return;
    
    try {
      const result = await messagingService.getConversations(user.id, currentOrganization.id);
      if (result.success && result.data) {
        // Filter out the current conversation
        const filtered = result.data.filter((conv) => conv.id !== conversation.id);
        setConversations(filtered);
      }
    } catch (error) {
      console.error('Error loading conversations for forward:', error);
    }
  };

  const handleForwardMessage = (message: Message) => {
    setForwardMessage(message);
    loadConversationsForForward();
    setShowForwardModal(true);
  };

  const handleForwarded = () => {
    // Refresh messages if needed
    setShowForwardModal(false);
    setForwardMessage(null);
  };

  const handleCreateTask = (message: Message) => {
    setTaskMessageContext({ message, conversation });
    setShowAddTaskPanel(true);
  };

  const handleTaskCreated = async (taskIds?: string[]) => {
    if (!taskMessageContext || !taskIds || taskIds.length === 0) {
      setShowAddTaskPanel(false);
      setTaskMessageContext(null);
      return;
    }

    // Link task to message and conversation
    const taskId = taskIds[0]; // Link first task to message
    const message = taskMessageContext.message;
    const conversation = taskMessageContext.conversation;

    try {
      // Update message with linked task
      if (message.id) {
        await messagingIntegrationService.linkTaskToMessage(message.id, taskId);
      }

      // Update conversation with linked task
      if (conversation.id) {
        await messagingIntegrationService.linkTaskToConversation(conversation.id, taskId);
      }

      // Update task with message context
      await messagingIntegrationService.updateTaskWithMessageContext(taskId, {
        createdFromMessageId: message.id,
        conversationId: conversation.id,
        lastDiscussedAt: new Date(),
      });
    } catch (error) {
      console.error('Error linking task to message:', error);
    }

    setShowAddTaskPanel(false);
    setTaskMessageContext(null);
    
    // Refresh messages to show updated links
    // The subscription will automatically update, but we can force a refresh
    if (conversation.id) {
      // Small delay to ensure database write is complete
      setTimeout(async () => {
        const result = await messagingService.getMessages(conversation.id!, 50);
        if (result.success && result.data) {
          setMessages(result.data);
        }
      }, 500);
    }
  };

  const handleViewTask = (task: TaskWithDetails) => {
    setViewingTask(task);
    setShowViewTaskModal(true);
  };

  const handleViewContract = (contract: AgreementDocument) => {
    // Open contract in a new window if it has a file URL
    if (contract.fileUrl) {
      window.open(contract.fileUrl, '_blank');
    } else {
      // If no file URL, just show notification
      // In the future, we could create a ViewContractModal similar to ViewTaskModal
      setViewingContract(contract);
      // For now, just open the contracts page
      window.open(`/agreements`, '_blank');
    }
  };

  const handleViewVendor = (vendor: Vendor) => {
    // Navigate to vendors page and open vendor detail modal
    // For now, just navigate to agreements page with vendors tab
    window.open(`/agreements`, '_blank');
  };

  const handleViewChecklist = async (completion: ChecklistCompletion) => {
    setViewingChecklistCompletion(completion);
    setIsLoadingChecklistDetails(true);

    try {
      if (completion.checklistId) {
        const checklistResult = await checklistService.getChecklist(completion.checklistId);
        if (checklistResult.success && checklistResult.data) {
          setViewingChecklist(checklistResult.data);
        }
      }
    } catch (error: any) {
      console.error('Error loading checklist details:', error);
      showNotification(`Error loading checklist details: ${error.message}`, 'error');
    } finally {
      setIsLoadingChecklistDetails(false);
    }
  };

  const handleCloseChecklistDetails = () => {
    setViewingChecklistCompletion(null);
    setViewingChecklist(null);
  };

  const handleMuteConversation = async () => {
    if (!conversation.id || !user?.id) return;
    
    try {
      const result = await messagingService.muteConversation(conversation.id, user.id);
      if (result.success) {
        // Update local state
        setParticipantData(prev => prev ? { ...prev, muted: true } : null);
        setShowMenuDropdown(false);
        showNotification('Conversation muted', 'success');
      } else {
        showNotification(`Failed to mute conversation: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error muting conversation:', error);
      showNotification(`Failed to mute conversation: ${error.message}`, 'error');
    }
  };

  const handleUnmuteConversation = async () => {
    if (!conversation.id || !user?.id) return;
    
    try {
      const result = await messagingService.unmuteConversation(conversation.id, user.id);
      if (result.success) {
        // Update local state
        setParticipantData(prev => prev ? { ...prev, muted: false } : null);
        setShowMenuDropdown(false);
        showNotification('Conversation unmuted', 'success');
      } else {
        showNotification(`Failed to unmute conversation: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error unmuting conversation:', error);
      showNotification(`Failed to unmute conversation: ${error.message}`, 'error');
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversation.id || !user?.id) return;
    
    if (!confirm('Are you sure you want to delete this conversation? It will be removed from your view, but will reappear if you receive new messages.')) {
      return;
    }
    
    try {
      const result = await messagingService.deleteConversation(conversation.id, user.id);
      if (result.success) {
        setShowMenuDropdown(false);
        showNotification('Conversation deleted', 'success');
        // Navigate back or close the conversation
        if (onBack) {
          onBack();
        }
      } else {
        showNotification(`Failed to delete conversation: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error deleting conversation:', error);
      showNotification(`Failed to delete conversation: ${error.message}`, 'error');
    }
  };

  const handleLoadMoreMessages = async () => {
    if (!conversation.id || loadingMore || !hasMoreMessages || messages.length === 0) {
      return;
    }

    setLoadingMore(true);

    try {
      // Get the oldest message's timestamp (first message in the array since they're sorted oldest to newest)
      const oldestMessage = messages[0];
      const oldestTimestamp = oldestMessage.createdAt;

      if (!oldestTimestamp) {
        setHasMoreMessages(false);
        setLoadingMore(false);
        return;
      }

      // Load 100 more messages before the oldest message
      const result = await messagingService.getMessages(conversation.id, 100, oldestTimestamp);

      if (result.success && result.data) {
        const loadedMessages = result.data;
        
        // Prepend new messages to existing messages
        setMessages((prevMessages) => {
          // Track that we've loaded more messages manually
          hasLoadedMoreRef.current = true;
          
          // Add new message IDs to our tracking set
          loadedMessages.forEach((msg) => {
            if (msg.id) {
              loadedMessageIdsRef.current.add(msg.id);
            }
          });
          
          return [...loadedMessages, ...prevMessages];
        });
        
        // If we got less than 100 messages, there are no more messages to load
        setHasMoreMessages(loadedMessages.length === 100);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error: any) {
      console.error('Error loading more messages:', error);
      setHasMoreMessages(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const displayName = (conversation.type === 'group' || conversation.type === 'task' || conversation.type === 'contract' || conversation.type === 'checklist' || conversation.type === 'form')
    ? conversation.name 
    : otherParticipantInfo?.name || 'Direct Message';

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header - Complin Style */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          {otherParticipantInfo?.picture || conversation.type === 'group' || conversation.type === 'task' || conversation.type === 'contract' || conversation.type === 'checklist' || conversation.type === 'form' ? (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {otherParticipantInfo?.picture ? (
                <img
                  src={otherParticipantInfo.picture}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Users className="w-6 h-6 text-gray-600" />
              )}
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <h2 className="text-gray-900 text-base font-medium truncate">{displayName}</h2>
            {conversationOrgName && (
              <p className="text-gray-500 text-xs truncate">{conversationOrgName}</p>
            )}
            {(conversation.type === 'group' || conversation.type === 'task' || conversation.type === 'contract' || conversation.type === 'checklist' || conversation.type === 'form') && conversation.description && (
              <p className="text-gray-600 text-xs truncate">{conversation.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {/* Show Participants button for group and contextual conversations */}
          {(conversation.type === 'group' || 
            conversation.type === 'task' || 
            conversation.type === 'contract' ||
            conversation.type === 'checklist' ||
            conversation.type === 'form' ||
            (conversation.participants && conversation.participants.length > 2)) && (
            <button
              onClick={() => setShowParticipantsModal(true)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="View Participants"
            >
              <Users className="w-5 h-5" />
            </button>
          )}
          <button
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            title="Search"
          >
            <Search className="w-5 h-5" />
          </button>
          <div className="relative" ref={menuDropdownRef}>
            <button
              onClick={() => setShowMenuDropdown(!showMenuDropdown)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="More options"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {showMenuDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {participantData?.muted ? (
                  <button
                    onClick={handleUnmuteConversation}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Bell className="w-4 h-4" />
                    <span>Unmute notifications</span>
                  </button>
                ) : (
                  <button
                    onClick={handleMuteConversation}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <BellOff className="w-4 h-4" />
                    <span>Mute notifications</span>
                  </button>
                )}
                <button
                  onClick={handleDeleteConversation}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete chat</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area - WhatsApp Background */}
      <div 
        className="flex-1 overflow-y-auto p-4"
        style={{ 
          backgroundColor: '#efeae2',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='whatsapp' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='25' cy='25' r='1' fill='%23d4d4d4' opacity='0.4'/%3E%3Ccircle cx='75' cy='75' r='1' fill='%23d4d4d4' opacity='0.4'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23whatsapp)'/%3E%3C/svg%3E")`
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-500">No messages yet. Start the conversation!</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Load More Button */}
            {hasMoreMessages && (
              <div className="flex justify-center py-2">
                <button
                  onClick={handleLoadMoreMessages}
                  disabled={loadingMore}
                  className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loadingMore ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      Loading...
                    </span>
                  ) : (
                    'Load More Messages'
                  )}
                </button>
              </div>
            )}
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.sentBy === user?.id}
                onForward={handleForwardMessage}
                onCreateTask={handleCreateTask}
                onViewTask={handleViewTask}
                onViewContract={handleViewContract}
                onViewVendor={handleViewVendor}
                onViewChecklist={handleViewChecklist}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-white">
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment, index) => (
              <div
                key={index}
                className={`relative ${
                  attachment.type === 'image' || attachment.type === 'video'
                    ? 'bg-white border border-gray-200 rounded-lg overflow-hidden'
                    : 'flex items-center space-x-2 bg-white border border-gray-200 rounded-lg p-2'
                }`}
              >
                {attachment.type === 'image' ? (
                  <>
                    <div className="relative">
                      <img
                        src={attachment.url}
                        alt={attachment.name}
                        className="max-w-[200px] max-h-[150px] object-cover"
                      />
                      <button
                        onClick={() => handleRemoveAttachment(index)}
                        className="absolute top-1 right-1 p-1 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-colors"
                        title="Remove image"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                    <div className="px-2 py-1">
                      <p className="text-xs text-gray-600 truncate max-w-[200px]" title={attachment.name}>
                        {attachment.name}
                      </p>
                      {attachment.size && (
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.size)}
                        </p>
                      )}
                    </div>
                  </>
                ) : attachment.type === 'video' ? (
                  <>
                    <div className="relative">
                      <video
                        src={attachment.url}
                        className="max-w-[200px] max-h-[150px] object-cover"
                        controls
                      />
                      <button
                        onClick={() => handleRemoveAttachment(index)}
                        className="absolute top-1 right-1 p-1 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-colors"
                        title="Remove video"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                    <div className="px-2 py-1">
                      <p className="text-xs text-gray-600 truncate max-w-[200px]" title={attachment.name}>
                        {attachment.name}
                      </p>
                      {attachment.size && (
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.size)}
                        </p>
                      )}
                    </div>
                  </>
                ) : attachment.type === 'audio' ? (
                  <>
                    <Mic className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 truncate max-w-[200px]">
                      {attachment.name}
                    </span>
                    {attachment.size && (
                      <span className="text-xs text-gray-500">
                        {formatFileSize(attachment.size)}
                      </span>
                    )}
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X className="w-3 h-3 text-gray-500" />
                    </button>
                  </>
                ) : (
                  <>
                    <File className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 truncate max-w-[200px]">
                      {attachment.name}
                    </span>
                    {attachment.size && (
                      <span className="text-xs text-gray-500">
                        {formatFileSize(attachment.size)}
                      </span>
                    )}
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X className="w-3 h-3 text-gray-500" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audio Recording Preview */}
      {(isRecording || audioUrl) && (
        <div className="px-4 py-3 border-t border-gray-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isRecording ? (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-700">
                    Recording... {formatRecordingTime(recordingTime)}
                  </span>
                  <button
                    onClick={stopAudioRecording}
                    className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Stop
                  </button>
                </>
              ) : (
                <>
                  <audio src={audioUrl || undefined} controls className="h-8" />
                  <button
                    onClick={sendAudioRecording}
                    disabled={uploading}
                    className="px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 transition-colors text-sm"
                  >
                    {uploading ? 'Uploading...' : 'Send Audio'}
                  </button>
                  <button
                    onClick={cancelAudioRecording}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Recording Preview */}
      {(isRecordingVideo || videoUrl) && (
        <div className="px-4 py-3 border-t border-gray-200 bg-red-50">
          <div className="flex flex-col space-y-3">
            {isRecordingVideo ? (
              <>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-700">
                    Recording video... {formatRecordingTime(recordingTime)}
                  </span>
                  <button
                    onClick={stopVideoRecording}
                    className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Stop
                  </button>
                </div>
                <video
                  ref={videoPreviewRef}
                  autoPlay
                  muted
                  playsInline
                  className="max-w-full max-h-48 rounded-lg"
                />
              </>
            ) : (
              <>
                <video
                  src={videoUrl || undefined}
                  controls
                  className="max-w-full max-h-48 rounded-lg"
                />
                <div className="flex items-center space-x-3">
                  <button
                    onClick={sendVideoRecording}
                    disabled={uploading}
                    className="px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 transition-colors text-sm"
                  >
                    {uploading ? 'Uploading...' : 'Send Video'}
                  </button>
                  <button
                    onClick={cancelVideoRecording}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Message Input - WhatsApp Style */}
      <div className="px-4 py-2 border-t border-gray-200 bg-[#f0f2f5]">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          {/* File Picker Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || isRecording || isRecordingVideo}
            className="flex-shrink-0 p-2.5 text-gray-600 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Attach file or image"
          >
            <Plus className="w-6 h-6" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Message Input with Audio Button Inside */}
          <div className="flex-1 relative bg-white rounded-3xl border border-gray-300">
            <div className="relative w-full">
              <textarea
                ref={messageInputRef}
                value={messageText}
                onChange={handleMessageTextChange}
                onKeyDown={handleKeyPress}
                placeholder="Type a message (use @ to mention)"
                rows={1}
                disabled={uploading || isRecording || isRecordingVideo}
                className="w-full px-4 py-2.5 pr-12 rounded-3xl resize-none focus:outline-none disabled:bg-gray-50 text-sm"
                style={{ minHeight: '42px', maxHeight: '120px' }}
              />
              {/* Mention Autocomplete - positioned above textarea next to @ */}
              {showMentionAutocomplete && (
                <div 
                  ref={mentionAutocompleteRef} 
                  className="fixed z-50"
                  style={{
                    top: `${mentionPosition.top - 10}px`, // Position just above the @ symbol
                    left: `${mentionPosition.left}px`,
                    transform: 'translateY(-100%)', // Move it above
                  }}
                >
                  <MentionAutocomplete
                    users={mentionUsers}
                    query={mentionQuery}
                    selectedIndex={mentionSelectedIndex}
                    onSelectUser={handleSelectMention}
                    onClose={() => setShowMentionAutocomplete(false)}
                    position={{ top: 0, left: 0 }}
                    visible={showMentionAutocomplete}
                  />
                </div>
              )}
            </div>
            {/* Audio and Video Recording Buttons Inside Input */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
              <button
                type="button"
                onClick={isRecordingVideo ? stopVideoRecording : startVideoRecording}
                disabled={uploading || sending || isRecording}
                className={`p-1.5 rounded-full transition-colors ${
                  isRecordingVideo
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={isRecordingVideo ? 'Stop video recording' : 'Record video'}
              >
                <Video className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={isRecording ? stopAudioRecording : startAudioRecording}
                disabled={uploading || sending || isRecordingVideo}
                className={`p-1.5 rounded-full transition-colors ${
                  isRecording
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={isRecording ? 'Stop recording' : 'Record audio'}
              >
                <Mic className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Send Button */}
          {messageText.trim() || attachments.length > 0 ? (
            <button
              type="submit"
              disabled={sending || uploading || isRecording || isRecordingVideo}
              className="flex-shrink-0 p-2.5 bg-gray-800 text-white rounded-full hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title="Send message"
            >
              <Send className="w-6 h-6" />
            </button>
          ) : null}
        </form>
      </div>

      {/* Forward Message Modal */}
      {showForwardModal && forwardMessage && (
        <ForwardMessageModal
          message={forwardMessage}
          conversations={conversations}
          onClose={() => {
            setShowForwardModal(false);
            setForwardMessage(null);
          }}
          onForwarded={handleForwarded}
        />
      )}

      {/* File Size Error Modal */}
      <FileSizeErrorModal
        fileName={fileSizeError?.fileName || ''}
        fileSize={fileSizeError?.fileSize || 0}
        isOpen={!!fileSizeError}
        onClose={() => setFileSizeError(null)}
      />

      {/* Add Task Panel - opened from message */}
      {showAddTaskPanel && taskMessageContext && currentOrganization?.id && (
        <AddTaskPanel
          onClose={() => {
            setShowAddTaskPanel(false);
            setTaskMessageContext(null);
          }}
          onTaskCreated={handleTaskCreated}
          prepopulatedTaskName={taskMessageContext.message.text.slice(0, 100)}
          prepopulatedOwnerRole={undefined}
          prepopulatedReferenceImageUrls={
            taskMessageContext.message.attachments
              ?.filter(att => att.type === 'image')
              .map(att => att.url)
              || []
          }
          isFromChecklist={false}
        />
      )}

      {/* View Task Modal */}
      {showViewTaskModal && viewingTask && (
        <ViewTaskModal
          task={viewingTask}
          onClose={() => {
            setShowViewTaskModal(false);
            setViewingTask(null);
          }}
        />
      )}

      {/* Participants Modal */}
      <ParticipantsModal
        conversation={conversation}
        isOpen={showParticipantsModal}
        onClose={() => setShowParticipantsModal(false)}
      />

      {/* Checklist Completion Details Right-Side Panel */}
      {viewingChecklistCompletion && (
        <>
          {/* Click-out overlay to close */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-[125]"
            onClick={handleCloseChecklistDetails}
          />
          <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[130] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Checklist Run Details
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {viewingChecklistCompletion.completedAt?.toDate 
                    ? viewingChecklistCompletion.completedAt.toDate().toLocaleString()
                    : typeof viewingChecklistCompletion.completedAt === 'number'
                    ? new Date(viewingChecklistCompletion.completedAt).toLocaleString()
                    : 'Unknown date'}
                </p>
              </div>
              <button
                onClick={handleCloseChecklistDetails}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {isLoadingChecklistDetails ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading details...</p>
                </div>
              ) : viewingChecklist && viewingChecklistCompletion ? (
                <>
                  {/* Checklist Completion Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-gray-700">Checklist:</span>{' '}
                        <span className="text-gray-900">{viewingChecklistCompletion.checklistName}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Location:</span>{' '}
                        <span className="text-gray-900">{viewingChecklistCompletion.locationName || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Completed By:</span>{' '}
                        <span className="text-gray-900">{(viewingChecklistCompletion.completedByName || viewingChecklistCompletion.completedBy) || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Checklist Items */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                      Checklist Items ({
                        viewingChecklistCompletion.items
                          ? Object.values(viewingChecklistCompletion.items || {}).filter((v: any) => v).length
                          : 0
                      }/{viewingChecklist.items?.length || 0} completed)
                    </h4>
                    <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[400px] overflow-y-auto">
                      {viewingChecklist.items?.map((item, index) => {
                        const completion = viewingChecklistCompletion;
                        // Use items (mobile format)
                        const items = completion.items || {};
                        const isCompleted = items[item.text] || false;
                        const photoData = completion.photos?.[item.text];
                        // Normalize photos to array: handle both string (single photo) and string[] (multiple photos)
                        const photos = photoData 
                          ? (Array.isArray(photoData) ? photoData : [photoData])
                          : [];
                        const taskRefs = (completion.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                        return (
                          <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-900">
                                <span className="font-medium">{index + 1}.</span> {item.text}
                                {isCompleted ? (
                                  <span className="ml-2 text-green-700">✓</span>
                                ) : (
                                  <span className="ml-2 text-gray-400">•</span>
                                )}
                              </div>
                              {isCompleted && taskRefs.length > 0 && (
                                <div className="mt-1 text-xs text-blue-600 font-medium">
                                  ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                                </div>
                              )}
                              {photos.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-2">
                                  {photos.map((photoUrl, photoIndex) => (
                                    <img
                                      key={photoIndex}
                                      src={photoUrl}
                                      alt={`${item.text} photo ${photoIndex + 1}`}
                                      className="max-w-full h-auto rounded border border-gray-300 max-h-32 cursor-pointer"
                                      onClick={() => window.open(photoUrl, '_blank')}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">Unable to load checklist details</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default MessageThread;
